import { useEffect, useRef, useState } from "react";
import API from "../../services/UserPreferencesService";
import coordinator from "../../services/calendarViewPreferenceCoordinator";
import { getStoredAuthToken, subscribeAuthTokenChanges } from "../../services/authTokenStorage";
import { getDefaultCalendarView, normalizeCalendarView } from "./calendarViews";

const defaultView = () => getDefaultCalendarView(typeof window === "undefined" ? 1024 : window.innerWidth);

export default function useCalendarViewPreference(accountId) {
  const [reload, setReload] = useState(0);
  const [state, setState] = useState({ accountId, view: defaultView(), loadStatus: "loading", saveStatus: "idle" });
  const selection = useRef(0);
  const currentAccount = useRef(accountId);
  currentAccount.current = accountId;

  useEffect(() => subscribeAuthTokenChanges(() => setReload(value => value + 1)), []);

  useEffect(() => {
    let active = true;
    const token = getStoredAuthToken();
    const pending = coordinator.getSnapshot(accountId);
    const initialRevision = pending.revision;
    const initialSelection = selection.current;
    const hasPending = pending.saveStatus !== "idle";
    const isCurrent = () => active && currentAccount.current === accountId && getStoredAuthToken() === token;
    setState({
      accountId,
      view: hasPending ? pending.view : defaultView(),
      saveStatus: pending.saveStatus,
      loadStatus: "loading",
    });
    const unsubscribe = coordinator.subscribe(accountId, () => {
      if (!isCurrent()) return;
      const snapshot = coordinator.getSnapshot(accountId);
      setState(value => ({
        ...value,
        view: snapshot.view || value.view,
        saveStatus: snapshot.saveStatus,
      }));
    });
    API.getCalendarView().then((response) => {
      if (!isCurrent()) return;
      const changed = selection.current !== initialSelection || coordinator.getSnapshot(accountId).revision !== initialRevision;
      setState(value => ({
        ...value,
        loadStatus: "ready",
        view: hasPending || changed ? value.view : normalizeCalendarView(response.calendarView) || defaultView(),
      }));
    }).catch(() => {
      if (!isCurrent()) return;
      setState(value => ({ ...value, loadStatus: selection.current === initialSelection && !hasPending ? "error" : "ready" }));
    });
    return () => { active = false; unsubscribe(); };
  }, [accountId, reload]);

  const selectView = (view) => {
    if (!normalizeCalendarView(view)) return;
    selection.current += 1;
    setState(value => ({ ...value, view, loadStatus: "ready" }));
    coordinator.select(accountId, view);
  };

  return {
    ...(state.accountId === accountId ? state : { view: defaultView(), loadStatus: "loading", saveStatus: "idle" }),
    selectView,
    retryLoad: () => setReload(value => value + 1),
    retrySave: () => coordinator.retry(accountId),
  };
}
